import React, { ReactNode } from 'react';
import { Box, Banner, InlineLink } from '@fullstory/components';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { fullstorySelectors, SpecObjectInfo } from '@fullstory/redux';
import { generateRoutePath, SETTINGS_SPEC } from '@fullstory/routes';

interface UnmappedSpecsWarningProps {
  /**
   * Array of unmapped spec object info
   */
  unmappedRecommendations?: (SpecObjectInfo | undefined)[];
}

/**
 * A component that displays a warning banner for unmapped spec recommendations
 * with a link to the spec settings page
 */
export const UnmappedSpecsWarning: React.FC<UnmappedSpecsWarningProps> = ({
  unmappedRecommendations,
}) => {
  const intl = useIntl();
  const orgId = useSelector(fullstorySelectors.getOrgId);

  if (!unmappedRecommendations || unmappedRecommendations.length === 0) {
    return null;
  }

  return (
    <Box padding={[16, 16, 0, 16]}>
      <Banner
        variant="warning"
        title={intl.formatMessage({
          defaultMessage: 'Unmapped recommendations',
          id: 'VrVmVp',
        })}
        description={intl.formatMessage(
          {
            defaultMessage:
              'This object contains unmapped recommendations: {specNames}. <a>Map recommended data objects</a>',
            id: 'md4tQL',
          },
          {
            specNames: unmappedRecommendations.map(spec => spec?.displayName).join(', '),
            a: (text: ReactNode) => (
              <InlineLink to={generateRoutePath(SETTINGS_SPEC, { orgId })}>{text}</InlineLink>
            ),
          },
        )}
      />
    </Box>
  );
};

export default UnmappedSpecsWarning;
